import React, { useState, useEffect } from 'react';
import { getBudgetOperationHistory, addBudgetOperationHistoryEntry } from '../utils/storage';

const BudgetOperationHistory = ({ operationId, operationName, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [newEntry, setNewEntry] = useState({
    action_type: 'action_added',
    description: '',
    user_name: ''
  });

  // Function to get current user from localStorage
  const getCurrentUser = () => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  };

  useEffect(() => {
    loadHistory();
    // Auto-populate user name
    const currentUser = getCurrentUser();
    if (currentUser) {
      setNewEntry(prev => ({
        ...prev,
        user_name: currentUser.name || currentUser.username || 'Usuario'
      }));
    }
  }, [operationId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const historyData = await getBudgetOperationHistory(operationId);
      setHistory(historyData);
    } catch (error) {
      console.error('Error al cargar historial:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!newEntry.description.trim()) return;

    try {
      // Get current user for auto-population
      const currentUser = getCurrentUser();
      const entryWithUser = {
        ...newEntry,
        user_name: newEntry.user_name || currentUser?.name || currentUser?.username || 'Usuario'
      };

      await addBudgetOperationHistoryEntry(operationId, entryWithUser);
      
      // Show success message
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
      // Reset form
      setNewEntry({
        action_type: 'action_added',
        description: '',
        user_name: currentUser?.name || currentUser?.username || 'Usuario'
      });
      setShowAddEntry(false);
      await loadHistory(); // Recargar historial
    } catch (error) {
      console.error('Error al agregar entrada:', error);
      alert('Error al agregar entrada al historial');
    }
  };

  const getActionTypeLabel = (actionType) => {
    const labels = {
      'created': '🆕 Creado',
      'updated': '✏️ Actualizado',
      'action_added': '⚡ Acción Agregada',
      'observation_added': '📝 Observación Agregada',
      'budget_increased': '💰 Presupuesto Modificado'
    };
    return labels[actionType] || actionType;
  };

  const getActionTypeColor = (actionType) => {
    const colors = {
      'created': 'bg-green-100 text-green-800',
      'updated': 'bg-blue-100 text-blue-800',
      'action_added': 'bg-purple-100 text-purple-800',
      'observation_added': 'bg-yellow-100 text-yellow-800',
      'budget_increased': 'bg-orange-100 text-orange-800'
    };
    return colors[actionType] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">📋 Historial de Operación</h2>
              <p className="text-blue-100 mt-1">{operationName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center animate-pulse">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">✅ Entrada agregada exitosamente al historial</span>
              </div>
            </div>
          )}

          {/* Add Entry Form */}
          {showAddEntry && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold mb-4">Agregar Nueva Entrada</h3>
              <form onSubmit={handleAddEntry} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Acción
                  </label>
                  <select
                    value={newEntry.action_type}
                    onChange={(e) => setNewEntry({...newEntry, action_type: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="action_added">⚡ Acción Agregada</option>
                    <option value="observation_added">📝 Observación Agregada</option>
                    <option value="budget_increased">💰 Presupuesto Modificado</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción *
                  </label>
                  <textarea
                    value={newEntry.description}
                    onChange={(e) => setNewEntry({...newEntry, description: e.target.value})}
                    placeholder="Describe la acción realizada..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>
                
                
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddEntry(false)}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* History Timeline */}
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Cargando historial...</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📝</div>
              <p>No hay entradas en el historial</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div key={entry.id} className="relative">
                  {/* Timeline line */}
                  {index < history.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200"></div>
                  )}
                  
                  {/* Timeline entry */}
                  <div className="flex items-start space-x-4">
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 w-12 h-12 bg-white border-4 border-blue-200 rounded-full flex items-center justify-center">
                      <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                    </div>
                    
                    {/* Entry content */}
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getActionTypeColor(entry.action_type)}`}>
                          {getActionTypeLabel(entry.action_type)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                      
                      {/* Parse and display description nicely */}
                      {entry.action_type === 'budget_increased' && entry.description.includes('DETALLES DEL CAMBIO') ? (
                        <div className="space-y-3">
                          {(() => {
                            // Extract user comment
                            const commentMatch = entry.description.match(/💬 COMENTARIO DEL USUARIO:\s*"([^"]+)"/);
                            const userComment = commentMatch ? commentMatch[1] : '';
                            
                            // Extract budget details with more flexible regex
                            const oldBudgetMatch = entry.description.match(/Presupuesto anterior:\s*\$([0-9.,]+)/);
                            const newBudgetMatch = entry.description.match(/Presupuesto nuevo:\s*\$([0-9.,]+)/);
                            const changeTypeMatch = entry.description.match(/Tipo de cambio:\s*([^\n•]+)/);
                            const differenceMatch = entry.description.match(/Diferencia:\s*\$([0-9.,]+)/);
                            const percentageMatch = entry.description.match(/Porcentaje de cambio:\s*([0-9.-]+)%/);
                            
                            const oldBudget = oldBudgetMatch ? parseFloat(oldBudgetMatch[1].replace(',', '')) : 0;
                            const newBudget = newBudgetMatch ? parseFloat(newBudgetMatch[1].replace(',', '')) : 0;
                            const changeType = changeTypeMatch ? changeTypeMatch[1].trim() : '';
                            const difference = differenceMatch ? parseFloat(differenceMatch[1].replace(',', '')) : Math.abs(newBudget - oldBudget);
                            const percentage = percentageMatch ? parseFloat(percentageMatch[1]) : (oldBudget > 0 ? ((newBudget - oldBudget) / oldBudget) * 100 : 0);
                            
                            return (
                              <>
                                {userComment && (
                                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                                    <p className="text-blue-800 font-medium">💬 Comentario:</p>
                                    <p className="text-blue-700 mt-1">"{userComment}"</p>
                                  </div>
                                )}
                                
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                  <h4 className="font-semibold text-orange-800 mb-3 flex items-center">
                                    💰 Cambio de Presupuesto
                                  </h4>
                                  
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-gray-600">Presupuesto anterior:</span>
                                      <div className="font-semibold text-red-600">${oldBudget.toFixed(2)}</div>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Presupuesto nuevo:</span>
                                      <div className="font-semibold text-green-600">${newBudget.toFixed(2)}</div>
                                    </div>
                                  </div>
                                  
                                  <div className="mt-3 pt-3 border-t border-orange-200">
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-gray-600">Tipo de cambio:</span>
                                      <span className={`font-semibold px-2 py-1 rounded ${
                                        newBudget > oldBudget ? 'bg-green-100 text-green-800' :
                                        newBudget < oldBudget ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {newBudget > oldBudget ? '↗️ Aumento' : newBudget < oldBudget ? '↘️ Reducción' : '➡️ Sin cambio'}
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center justify-between text-sm mt-2">
                                      <span className="text-gray-600">Diferencia:</span>
                                      <span className="font-semibold">${difference.toFixed(2)}</span>
                                    </div>
                                    
                                    <div className="flex items-center justify-between text-sm mt-2">
                                      <span className="text-gray-600">Porcentaje:</span>
                                      <span className={`font-semibold ${
                                        percentage > 0 ? 'text-green-600' : 
                                        percentage < 0 ? 'text-red-600' : 'text-gray-600'
                                      }`}>
                                        {percentage > 0 ? '+' : ''}{percentage.toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="text-gray-800 mb-2">
                          <p className="whitespace-pre-wrap">{entry.description}</p>
                        </div>
                      )}
                      
                      {entry.user_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="mr-1">👤</span>
                          <span>Por: {entry.user_name}</span>
                        </div>
                      )}
                      
                      {/* Show field changes if available */}
                      {entry.field_changed && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <strong>Campo modificado:</strong> {entry.field_changed}
                          {entry.old_value && entry.new_value && (
                            <div className="mt-1">
                              <span className="text-red-600">Anterior: {entry.old_value}</span>
                              <span className="mx-2">→</span>
                              <span className="text-green-600">Nuevo: {entry.new_value}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetOperationHistory;
